import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";
import { getVillaTypes, createVillaType, updateVillaType, deleteVillaType } from '../../services/master';

const VillaType = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [villaTypeData, setVillaTypeData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedVillaTypeForDelete, setSelectedVillaTypeForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingVillaType, setEditingVillaType] = useState(null);
  const [newVillaType, setNewVillaType] = useState({
    villaType: '',
    status: 'Active',
  });

  // Fetch villa types
  const fetchVillaTypes = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const villaTypes = await getVillaTypes();
      setVillaTypeData(villaTypes);
    } catch (err) {
      setApiError('Failed to load villa types.');
      setVillaTypeData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVillaTypes();
  }, []);

  // Filter logic
  const getFilteredVillaTypes = () => {
    return villaTypeData.filter((vt) => {
      const matchesSearch =
        vt.villaType.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || vt.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredVillaTypes = getFilteredVillaTypes();

  // Pagination
  const paginatedVillaTypes = filteredVillaTypes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newVillaType.villaType.trim()) err.villaType = 'Villa Type is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return villaTypeData.some(
      (vt) =>
        vt.villaType.toLowerCase() === newVillaType.villaType.trim().toLowerCase() &&
        vt.id !== editingVillaType?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingVillaType(null);
    setNewVillaType({
      villaType: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingVillaType(null);
    setNewVillaType({
      villaType: '',
      status: 'Active',
    });
    setErrors({});
  };

  const handleEdit = (villaType) => {
    setEditingVillaType(villaType);
    setNewVillaType({
      villaType: villaType.villaType,
      status: villaType.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (villaType) => {
    setSelectedVillaTypeForDelete(villaType);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedVillaTypeForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      await deleteVillaType(selectedVillaTypeForDelete.id);
      setAlertConfig({ title: 'Success', message: 'Villa type deleted successfully!' });
      setAlertOpen(true);
      fetchVillaTypes(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete villa type.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedVillaTypeForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ villaType: 'Villa type already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'villaType',
        details: {
          villaType: newVillaType.villaType.trim(),
        },
        status: newVillaType.status === 'Active',
      };

      if (editingVillaType) {
        await updateVillaType(editingVillaType.id, payload);
      } else {
        await createVillaType(payload);
      }

      setAlertConfig({
        title: 'Success',
        message: editingVillaType ? 'Villa type updated successfully!' : 'Villa type added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchVillaTypes(true);
    } catch (err) {
      setErrors({ villaType: 'Failed to save villa type.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
    fetchVillaTypes(true);
  };

  const handleChange = (field, value) => {
    setNewVillaType((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Villa Type Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchVillaTypes()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
            >
              Add Villa Type
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the villa type <strong>{selectedVillaTypeForDelete?.villaType}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchVillaTypes()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Villa Type..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredVillaTypes.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} villa types matching your criteria.` : 'No villa types found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !villaTypeData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Villa Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedVillaTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No villa types found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedVillaTypes.map((villaType, index) => (
                    <TableRow key={villaType.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{villaType.villaType}</TableCell>
                      <TableCell>
                        <Chip
                          label={villaType.status}
                          color={villaType.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleEdit(villaType)} disabled={isLoading}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(villaType)} disabled={isLoading}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredVillaTypes.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingVillaType ? 'Edit Villa Type' : 'Add New Villa Type'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Villa Type"
                  value={newVillaType.villaType}
                  onChange={(e) => handleChange('villaType', e.target.value)}
                  error={!!errors.villaType}
                  helperText={errors.villaType}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newVillaType.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingVillaType ? 'Update' : 'Add'} Villa Type
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default VillaType;
